import React, { useState } from "react";
import { toast } from "../../../utils/notify";
import { DataTable } from "../../common/DataTable";

const STATUS_LABEL = {
  implemented: "Implemented",
  partially_implemented: "Partially Implemented",
  under_review: "Under Review",
  in_progress: "In progress",
  not_started: "Not started",
};

const STATUS_PILL_CLASS = {
  implemented: "bg-green-50 text-green-700 border-green-200",
  partially_implemented: "bg-amber-50 text-amber-700 border-amber-200",
  under_review: "bg-blue-50 text-blue-700 border-blue-200",
  in_progress: "bg-blue-50 text-blue-700 border-blue-200",
  not_started: "bg-slate-100 text-slate-600 border-slate-200",
};

function initials(label) {
  if (!label) return "?";
  const parts = label.trim().split(/\s+/);
  return (parts[0]?.[0] || "").concat(parts[1]?.[0] || "").toUpperCase() || label[0]?.toUpperCase();
}

function DueCell({ control }) {
  if (control.status === "implemented") {
    const d = control.completedAt ? new Date(control.completedAt) : null;
    return <span className="text-xs text-gray-500">Completed {d ? d.toLocaleDateString() : ""}</span>;
  }
  if (!control.dueDate) return <span className="text-xs text-gray-400">-</span>;
  const due = new Date(control.dueDate);
  const days = Math.ceil((due - new Date()) / (1000 * 60 * 60 * 24));
  if (days < 0) {
    return <span className="text-xs font-semibold text-red-600">Overdue {Math.abs(days)}d</span>;
  }
  if (days <= 7) {
    return <span className="text-xs font-semibold text-amber-600">Due in {days}d</span>;
  }
  return <span className="text-xs text-gray-500">{due.toLocaleDateString()}</span>;
}

const TABS = [
  { id: "all", label: "All" },
  { id: "implemented", label: "Implemented" },
  { id: "pending", label: "Pending" },
  { id: "assigned", label: "Assigned" },
  { id: "unassigned", label: "Unassigned" },
];

export default function ControlsTable({
  controls,
  loading,
  error,
  count,
  page,
  pageSize = 12,
  totalPages,
  tabCounts,
  tab,
  onTabChange,
  search,
  onSearchChange,
  category,
  onCategoryChange,
  categories = [],
  framework,
  onFrameworkChange,
  frameworks = [],
  showFrameworkFilter = false,
  flag,
  onClearFlag,
  owners = [],
  onPageChange,
  onPatch,
  onClearFilters,
}) {
  const [rowError, setRowError] = useState({});

  const handleOwnerChange = async (control, ownerId) => {
    const prev = control.ownerId;
    const ok = await onPatch(control.id, { ownerId: ownerId || null });
    if (!ok) {
      setRowError((s) => ({ ...s, [control.id]: "owner" }));
      toast?.error?.("Failed to update owner.");
    } else {
      const copy = { ...rowError };
      delete copy[control.id];
      setRowError(copy);
    }
  };

  const columns = [
    {
      id: "controlRef",
      header: "Control ID",
      cell: ({ row }) => (
        <div className="align-top font-mono text-xs text-gray-600">
          {row.original.controlRef}
          {row.original.frameworkName && (
            <div className="mt-1 inline-block text-[10px] font-medium text-indigo-600 bg-indigo-50 rounded-full px-2 py-0.5">
              {row.original.frameworkName}
            </div>
          )}
        </div>
      ),
    },
    {
      id: "control",
      header: "Control",
      cell: ({ row }) => (
        <div>
          <div className="font-medium text-gray-900">{row.original.title}</div>
          <div className="text-xs text-gray-500">{row.original.category}</div>
        </div>
      ),
    },
    {
      id: "owner",
      header: "Owner",
      cell: ({ row }) => {
        const control = row.original;
        return (
          <div className="flex items-center gap-2">
            <div className="w-7 h-7 rounded-full bg-indigo-100 text-indigo-700 text-[11px] font-semibold flex items-center justify-center flex-shrink-0">
              {control.ownerLabel ? initials(control.ownerLabel) : "?"}
            </div>
            <select
              value={control.ownerId || ""}
              onChange={(e) => handleOwnerChange(control, e.target.value)}
              className={`text-xs border rounded-md px-2 py-1 ${rowError[control.id] === "owner" ? "border-red-300" : "border-gray-200"}`}
            >
              <option value="">Unassigned</option>
              {owners.map((o) => (
                <option key={o.id} value={o.id}>{o.name}</option>
              ))}
              {control.ownerId && !owners.find((o) => o.id === control.ownerId) && (
                <option value={control.ownerId}>{control.ownerLabel}</option>
              )}
            </select>
          </div>
        );
      },
    },
    {
      id: "status",
      header: "Status",
      cell: ({ row }) => {
        const control = row.original;
        return (
          <span
            className={`text-xs font-medium border rounded-full px-2 py-1 ${STATUS_PILL_CLASS[control.status] || STATUS_PILL_CLASS.not_started}`}
            title="Auto-updates from uploaded/approved files"
          >
            {STATUS_LABEL[control.status] || control.status}
          </span>
        );
      },
    },
    {
      id: "due",
      header: "Due",
      cell: ({ row }) => <DueCell control={row.original} />,
    },
    {
      id: "evidence",
      header: "Evidence",
      cell: ({ row }) => <span className="text-xs text-gray-600">{row.original.evidenceCount}</span>,
    },
  ];

  return (
    <div className="bg-white rounded-xl border border-gray-200 overflow-hidden">
      {/* Toolbar */}
      <div className="p-4 flex flex-col sm:flex-row gap-3 border-b border-gray-200">
        <input
          type="text"
          value={search}
          onChange={(e) => onSearchChange(e.target.value)}
          placeholder="Search control ref, title, category…"
          className="flex-1 border border-gray-200 rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-indigo-200"
        />
        {categories.length > 0 && (
          <select
            value={category || ""}
            onChange={(e) => onCategoryChange(e.target.value || "")}
            className="border border-gray-200 rounded-lg px-3 py-2 text-sm"
          >
            <option value="">All categories</option>
            {categories.map((c) => (
              <option key={c} value={c}>{c}</option>
            ))}
          </select>
        )}
        {showFrameworkFilter && (
          <select
            value={framework || ""}
            onChange={(e) => onFrameworkChange(e.target.value || "")}
            className="border border-gray-200 rounded-lg px-3 py-2 text-sm"
          >
            <option value="">All frameworks</option>
            {frameworks.map((f) => (
              <option key={f.id} value={f.id}>{f.name}</option>
            ))}
          </select>
        )}
      </div>

      {flag && (
        <div className="px-4 pt-3 flex items-center gap-2">
          <span className="inline-flex items-center gap-1 text-xs font-medium bg-amber-50 text-amber-700 border border-amber-200 rounded-full px-3 py-1">
            {flag === "overdue" ? "Overdue only" : "Due within 7 days"}
            <button type="button" onClick={onClearFlag} aria-label="Clear filter" className="ml-1 text-amber-500 hover:text-amber-700">
              ×
            </button>
          </span>
        </div>
      )}

      {/* Tabs */}
      <div role="tablist" className="flex gap-1 px-4 pt-3 border-b border-gray-200 overflow-x-auto">
        {TABS.map((t) => (
          <button
            key={t.id}
            role="tab"
            aria-selected={tab === t.id}
            onClick={() => onTabChange(t.id)}
            className={`px-3 py-2 text-sm font-medium rounded-t-lg border-b-2 whitespace-nowrap focus:outline-none focus-visible:ring-2 focus-visible:ring-indigo-300 ${
              tab === t.id
                ? "border-indigo-600 text-indigo-700"
                : "border-transparent text-gray-500 hover:text-gray-800"
            }`}
          >
            {t.label} ({tabCounts?.[t.id] ?? 0})
          </button>
        ))}
      </div>

      {/* Table */}
      <DataTable
        columns={columns}
        data={controls}
        getRowId={(control) => String(control.id)}
        loading={loading}
        error={error}
        emptyMessage={
          <div>
            <p className="text-sm text-gray-500 mb-3">No controls match</p>
            <button
              type="button"
              onClick={onClearFilters}
              className="text-sm font-medium text-indigo-600 hover:text-indigo-800"
            >
              Clear filters
            </button>
          </div>
        }
        manualPagination
        rowCount={count}
        pagination={{ pageIndex: (page || 1) - 1, pageSize }}
        onPaginationChange={(updater) => {
          const current = { pageIndex: (page || 1) - 1, pageSize };
          const next = typeof updater === "function" ? updater(current) : updater;
          onPageChange(next.pageIndex + 1, next.pageSize);
        }}
        enableColumnVisibility={false}
        enableColumnResizing
        maxBodyHeight="65vh"
      />
    </div>
  );
}
